import React, { useCallback, useContext, useEffect, useState } from "react";
import { Pagination, Table, Tooltip } from "antd";
import { MdRequestQuote } from "react-icons/md";
import { useSelector } from "react-redux";
import { useHistory } from "react-router";
import { isArray } from "lodash";
import { ColumnModel, DocTypeRename, FeatureFlags, StatusRename } from "../hooks/featureFlagsProvider";
import { explodeFileName, timeSpent, formatShortDate } from "../utils";
import { RootState, useAppSelector } from "../store";
import UserPresenceIcon from "./UserPresenceIcon";
import { Email, IPresenceUser } from "../types";
import { Filter } from "../pages/Dashboard";

interface Props {
  filter: Filter | null;
  onFilterChange: (key: keyof Filter, value?: number | string) => void;
  selectedEmails: React.Key[];
  onSelectionChange: (selectedRows: React.Key[]) => void;
}

function EmailsTable({ filter, onFilterChange, selectedEmails, onSelectionChange }: Props) {
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);

  const presenceUsers: IPresenceUser[] = useAppSelector((state) => state.signalRHub.presenceUsers);
  const urlParams = new URLSearchParams(window.location.search);
  const [columns, setColumns] = useState([]);
  const { data, loading, totalCount } = useSelector((state: RootState) => state.emails);
  const { features } = useContext(FeatureFlags);
  const statusRename: StatusRename[] = features.statusRename;
  const docTypeRename: DocTypeRename[] = features.docTypeRename;
  const history = useHistory();

  const handleRecord = useCallback(
    (c: ColumnModel, entity: Email) => {
      let data = "";
      if (entity && entity[c.dbPropertyName]) {
        data = entity[c.dbPropertyName];
      }
      let date = "";

      switch (c.renderType) {
        case "icons":
          switch (c.dbPropertyName) {
            case "isQuote":
              return entity.isQuote ? <Tooltip title="Contains Potential Quote Document">{<MdRequestQuote style={{ fontSize: "large" }} />}</Tooltip> : null;
            default:
              const presenceUsersForEntity = presenceUsers.filter(users => users.entityId === entity.id);
              return <UserPresenceIcon presenceUsers={presenceUsersForEntity}></UserPresenceIcon>;
          }
        case "filename":

          const filename = explodeFileName(data);
          if (filename) {
            return filename.filename;
          }
          return data;
        case "status":
          if (statusRename && isArray(statusRename)) {
            const status = statusRename.find((s) => s.status === data);
            if (status) {
              return status.displayAs ?? data;
            }
          }
          return data;
        case "text":
          if (c.dbPropertyName === "docType") return docTypeRename.find((sr: any) => sr.docType === data)?.displayAs ?? data;
          return data;
        case "date":
          date = formatShortDate(data, true);
          if (date) return <div>{date} - <b>{timeSpent(data)}</b></div>
          return "";
        case "dateLocal":
          date = formatShortDate(data, true, true);
          if (date) return <div>{date} - <b>{timeSpent(data, true)}</b></div>
          return "";
      }

      return "";
    },
    [presenceUsers, statusRename, docTypeRename]
  );

  const setHeaderIcon = useCallback(
    (labelIcon: string) => {
      switch (labelIcon) {
        case "isQuote":
          return <MdRequestQuote style={{ fontSize: "large" }} />;
        default:
          return "";
      }
    }, []
  );

  useEffect(() => {
    const displayCols = [];
    const columns = features?.displayColumns?.filter((c) => c.displayOrder >= 0);
    if (columns) {
      columns.sort((a, b) => a.displayOrder - b.displayOrder);
      columns.forEach((c: ColumnModel) => {
        let column = {
          title: c.renderType === 'icons' ? setHeaderIcon(c.label) : c.label,
          dataIndex: c.dbPropertyName,
          sorter: c.isSortable,
          render: (_, record) => handleRecord(c, record),
        };

        if (c.showIfStatus === undefined || c.showIfStatus === filter?.status) {
          displayCols.push(column);
        }
      });
    }

    setColumns(displayCols);
  }, [features, filter, handleRecord, setHeaderIcon]);

  const onRowClick = (record: Email, rowIndex?: number) => {
    history.push(`/invoice/${record.id}`);
  };

  const handleChange = (pagination, filters, sorter) => {
    if (sorter.order && sorter.order === "ascend") {
      onFilterChange("sortDirection", sorter.order.slice(0, 3));
    }
    if (sorter.order && sorter.order === "descend") {
      onFilterChange("sortDirection", sorter.order.slice(0, 4));
    }
    if (sorter.field) {
      onFilterChange("sortColumn", sorter.field);
    }
  };

  const pageIndex = +urlParams.get("page") || 1;
  const pageCount = +urlParams.get("count") || +sessionStorage.getItem(`count`) || 20;
  let beginIndex = (data.length ?? 0) > 0 ? (pageIndex - 1) * pageCount + 1 : 0;
  let endIndex = beginIndex > 0 ? beginIndex + (data.length ?? 0) - 1 : 0;
  if (endIndex > totalCount) endIndex = totalCount;

  const rowSelection = {
    onChange: (selectedRowKeys: React.Key[]) => {
      onSelectionChange(selectedRowKeys);
      setSelectedRowKeys(selectedRowKeys);
    }
  };

  useEffect(() => {
    setSelectedRowKeys(selectedEmails);
  }, [selectedEmails]);

  return (
    <>
      <Table
        className="custom-table"
        onRow={(record, rowIndex) => ({
          onClick: (e) => onRowClick(record, rowIndex),
        })}
        loading={loading}
        rowKey={(e) => e.id}
        columns={columns}
        rowSelection={features.isAssignToEnabled ? {
          type: 'checkbox',
          selectedRowKeys,
          ...rowSelection
        } : null}
        dataSource={data}
        pagination={
          features.pageLayout.useDefaultLayout && {
            onChange: (page, pageSize) => {
              const prevPageSize = sessionStorage.getItem(`count`);

              onFilterChange("page", page);

              if (prevPageSize !== pageSize.toString()) onFilterChange("count", pageSize);
            },

            total: totalCount,
            pageSize: +urlParams.get("count") || +sessionStorage.getItem(`count`) || 20,
            showSizeChanger: true,
            current: +urlParams.get("page") || 1,
          }
        }
        onChange={handleChange}
      />

      {!features.pageLayout.useDefaultLayout && (
        <>
          <div className="custom-pager pt-20px fs-11px">
            Showing results {beginIndex} to {endIndex} out of {totalCount ?? 0}
          </div>
          <Pagination
            className="custom-pager pt-10px"
            total={totalCount}
            size="small"
            showSizeChanger={false}
            current={pageIndex}
            pageSize={pageCount}
            onChange={(page, pageSize) => {
              const prevPageSize = sessionStorage.getItem(`count`);

              onFilterChange("page", page);

              if (prevPageSize !== pageSize.toString()) onFilterChange("count", pageSize);
            }}
          />
        </>
      )}
    </>
  );
}

export default EmailsTable;
